<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import { cn } from '@/lib/cn'
import { buttonVariants, type ButtonVariant } from './variants'
import type { IconName } from './icons'
import Icon from './Icon.vue'

defineOptions({ inheritAttrs: false })

const p = withDefaults(defineProps<{
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
  iconStart?: IconName
  iconEnd?: IconName
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit'
  /** Renders a link (RouterLink) that looks like a button. */
  to?: RouteLocationRaw
}>(), { variant: 'primary', size: 'md', type: 'button' })

const attrs = useAttrs()
const classes = computed(() =>
  cn(buttonVariants({ variant: p.variant, size: p.size, block: p.block }), attrs.class as string))
const rest = computed(() => {
  const { class: _c, ...others } = attrs
  return others
})
</script>

<template>
  <RouterLink v-if="to" :to="to" :class="classes" v-bind="rest">
    <Icon v-if="iconStart" :name="iconStart" />
    <slot />
    <Icon v-if="iconEnd" :name="iconEnd" />
  </RouterLink>
  <button v-else :type="type" :class="classes" :disabled="disabled || loading"
          :aria-busy="loading || undefined" v-bind="rest">
    <Icon v-if="loading" name="refresh" class="animate-spin" />
    <Icon v-else-if="iconStart" :name="iconStart" />
    <slot />
    <Icon v-if="!loading && iconEnd" :name="iconEnd" />
  </button>
</template>
